import { useInfiniteQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';

export interface TransactionDTO {
  date: string;
  description: string;
  accountNumber: string;
  notes?: string;
  name: string;
  price: number;
  total: number;
  units: number;
  confirmNotes: string | null;
}

const fetchTransactions = async ({
  clientId,
  accountId,
  groupId,
  query,
  period,
  startDate,
  endDate,
  sortBy,
  sortDesc,
  pageParam = 0,
}: {
  clientId: string;
  accountId?: string;
  groupId?: string;
  query?: string;
  period?: number;
  startDate?: string;
  endDate?: string;
  sortBy: string;
  sortDesc: boolean;
  pageParam: number;
}): Promise<TransactionDTO[]> =>
  authFetch(
    urlWithParams(
      '/Consumer/OrionAccountsWidgets/Transactions',
      {
        clientId,
        accountId: accountId || null,
        groupId: groupId || null,
        filter: query || null,
        period,
        startDate,
        endDate,
        sortBy,
        sortDesc,
        page: pageParam,
      },
      { skipNulls: true },
    ),
  );

export const useTransactionsQuery = (
  {
    clientId,
    accountId,
    groupId,
    query,
    period,
    startDate: _startDate,
    endDate: _endDate,
    sortBy,
    sortDesc,
  }: {
    clientId: string;
    accountId?: string;
    groupId?: string;
    query?: string;
    period?: number;
    startDate?: string;
    endDate?: string;
    sortBy: string;
    sortDesc: boolean;
  },
  { enabled }: { enabled: boolean } = { enabled: true },
) => {
  const startDate = period === undefined ? _startDate : undefined;
  const endDate = period === undefined ? _endDate : undefined;

  return useInfiniteQuery(
    queryKeys.transactions({
      clientId,
      accountId,
      groupId,
      period,
      startDate,
      endDate,
      query,
      sortBy,
      sortDesc,
    }),
    ({ pageParam }) =>
      fetchTransactions({
        clientId,
        accountId,
        groupId,
        query,
        period,
        startDate,
        endDate,
        sortBy,
        sortDesc,
        pageParam,
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length === 20) {
          return allPages.length;
        }

        return undefined;
      },
      enabled,
      ...(period === undefined ? { cacheTime: 0 } : {}), // Do not cache custom ranges
    },
  );
};
